import {Component, OnInit, ViewChild, ChangeDetectorRef, TemplateRef, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {DataService} from '../../../../Services/data.service';
import {ActivatedRoute, Router} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {ConfirmDialogService} from '../../../../Services/confirm-dialog.service';
import {BsToastService} from '../../../../Services/bs-toast-service';
import {Title} from '@angular/platform-browser';
import {Modal} from 'bootstrap';
import {lastValueFrom} from 'rxjs';
import {StripePaymentService} from '../../../../Services/stripe-payment.service';
import {NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {StripePaymentElementComponent, StripeService} from 'ngx-stripe';
import {StripeConnectApService} from '../../../../Services/stripe-connect-ap.service';
import {ApPayoutReadinessResponse, GetApPayoutReadinessRequest} from '../../../../DataModels/stripe-connect-ap.models';
import {EmailModel} from '../../../../DataModels/email-model';
import {
  extractRuntimeSetupIntentValues,
  resolveSetupEnvelope,
  STRIPE_FLOW_CONTRACTS,
  StripeSetupFlowKey,
  StripeSetupFlowType,
  computeAchStatusView,
  mapStripeFlowDto,
  mapStripeGlobalDto,
  buildEnsureCustomerPayload,
  normalizeAchStatusValue,
  isStatusPendingLike,
  getClientSecretIntentType,
  isSuccessfulStripeResponse,
} from './stripe-admin-form.helpers';

@Component({
  selector: 'app-stripe-admin-form',
  templateUrl: './stripe-admin-form.component.html',
  styleUrls: ['./stripe-admin-form.component.css'],
  standalone: false
})
export class StripeAdminFormComponent implements OnInit {
  stripeAdminID: string;
  stripeAdminEntityId = '';
  stripeAdminEntityName = '';
  isNewSetupMode = false;
  entityOptions: any[] = [];
  bankOptions: any[] = [];
  stripeAdminForm: UntypedFormGroup;
  activeTab: 'setup' | 'transactions' = 'setup';
  submitted = false;
  stripeAutoConfigLoading = false;
  stripeAchSetupLoading = false;
  stripeAchConfirmLoading = false;
  flowActivationUpdateLoading: 'ar' | 'ap' | '' = '';
  transactionsLoading = false;
  // Optional per-row loading tracker keyed by PaymentIntentId
  transactionsRowLoading: Record<string, boolean> = {};
  transactions: any[] = [];
  selectedTransactions: Record<string, boolean> = {};
  transactionsPage = 1;
  transactionsPageSize = 20;
  transactionsPageSizeOptions: number[] = [10, 20, 50, 100];
  transactionsTotalRecords = 0;
  transactionsStartIndex = 0;
  transactionsEndIndex = 0;
  transactionsFromDate: NgbDateStruct | string | null = null;
  transactionsToDate: NgbDateStruct | string | null = null;
  transactionsStatus = 'All';
  // AR and AP have different status option sets shown in the Transactions tab selector
  readonly arTransactionStatuses: string[] = ['All', 'Pending', 'RequiresAction', 'Processing', 'Succeeded', 'Failed', 'Canceled', 'Refunded'];
  readonly apTransactionStatuses: string[] = ['All', 'Pending', 'Processing', 'Succeeded', 'Failed', 'Canceled'];
  achSetupStatusType: 'success' | 'info' | 'warning' | 'danger' | '' = '';
  achSetupStatusMessage = '';
  pendingSetupStatus: 'none' | 'pending' | 'ready' = 'none';
  pendingSetupMessage = '';
  pendingVerificationUrl = '';
  pendingSetupClientSecret = '';
  pendingSetupFlow: 'ar' | 'ap' | '' = '';
  stripeInstantVerifyDialogOpen = false;
  pendingSetupElementReady = false;
  microdepositVerifyFlow: 'ar' | 'ap' = 'ap';
  scopedFlow: 'AR' | 'AP' | '' = '';
  queryParamEntityId = '';
  setupLoadCompleted = false;
  lastStripeProcess = '';
  lastStripeSetupRefreshEndpoint = '';
  microdepositVerifyModalOpen = false;
  microdepositVerifyMode: 'amounts' | 'descriptor' = 'amounts';
  microdepositAmount1 = '';
  microdepositAmount2 = '';
  microdepositDescriptorCode = '';
  // Track instant-verify failure per flow to steer user to microdeposit when needed
  instantVerifyFailed: { ar: boolean; ap: boolean } = {ar: false, ap: false};

  // Prevent duplicate instant‑verify launches per flow
  private verifyInFlight: { ar: boolean; ap: boolean } = {ar: false, ap: false};

  // One-line trace badge for Launch ACH Setup progress
  achSetupTracing = false;
  achSetupTraceStage: 0 | 1 | 2 | 3 | 4 = 0; // 1: ensure, 2: setup-intent, 3: refresh, 4: done

  @ViewChild('pendingSetupPaymentElement')
  pendingSetupPaymentElement?: StripePaymentElementComponent;

  // Templates for datatable cells (captured from HTML via template refs)
  @ViewChild('statusCell', {read: TemplateRef, static: false})
  private statusCellTpl?: TemplateRef<any>;

  @ViewChild('cancellableCell', {read: TemplateRef, static: false})
  private cancellableCellTpl?: TemplateRef<any>;

  @ViewChild('missingCell', {read: TemplateRef, static: false})
  private missingCellTpl?: TemplateRef<any>;

  @ViewChild('paymentIntentCell', {read: TemplateRef, static: false})
  private paymentIntentCellTpl?: TemplateRef<any>;

  @ViewChild('stripeTransferCell', {read: TemplateRef, static: false})
  private stripeTransferCellTpl?: TemplateRef<any>;

  stripeSetup = {
    ar: this.createDefaultStripeFlow(),
    ap: this.createDefaultStripeFlow(),
  };
  stripeGlobal = this.createDefaultStripeGlobal();

  // ---- AP (Connect Express) readiness state (Admin only, AR untouched) ----
  apReadinessLoading = false;
  apReadiness: ApPayoutReadinessResponse | null = null;
  // AP Balances
  apBalanceLoading = false;
  apBalance: any = null;
  emailObject: EmailModel = new EmailModel();

  buttonConfig: any = {
    save: true,
    close: true,
    reset: true,
    delete: true,
    saveNew: false,
    saveClose: true,
    print: false,
  };

  constructor(
    private table: DataService,
    private notification: BsToastService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private confirmDialogService: ConfirmDialogService,
    private titleService: Title,
    private stripePaymentService: StripePaymentService,
    private stripeService: StripeService,
    private stripeApService: StripeConnectApService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
  ) {
    this.createForm();
    this.applyRouteId((this.route.snapshot.params.id || '').toString().trim());
    this.applyFlowScope((this.route.snapshot.queryParamMap.get('flow') || '').toString().trim());
    this.applyQueryEntityScope((this.route.snapshot.queryParamMap.get('entityId') || '').toString().trim());
  }


  // ---- AP Readiness (Connect Express) — Admin only ----
  async checkApReadiness(showToast = true): Promise<void> {
    try {
      this.apReadinessLoading = true;
      const appEntityId = (this.f['entityId']?.value || this.stripeAdminEntityId || '').toString().trim();
      const appBankId = this.resolveEffectiveAppBankId();
      if (!appEntityId) {
        if (showToast) {
          this.notification.showWarningToast('Select an entity to check AP payout readiness.');
        }
        return;
      }
      const origin = (window?.location?.origin || '').replace(/\/$/, '');
      const returnUrl = `${origin}/vendors/${appEntityId}/ap/stripe/return`;
      const refreshUrl = `${origin}/vendors/${appEntityId}/ap/stripe/refresh`;
      const connectedAccountId = (this as any)?.stripeSetup?.ap?.connectedAccountId
        || (this as any)?.stripeSetup?.Ap?.connectedAccountId
        || undefined;

      const req: GetApPayoutReadinessRequest = {
        appEntityId,
        appBankId,
        connectedAccountId: connectedAccountId ?? null,
        returnUrl,
        refreshUrl,
      };
      const res = await lastValueFrom(this.stripeApService.getReadiness(req));
      try {
        console.log('[Stripe Admin][AP Readiness] response', {
          appEntityId,
          appBankId,
          connectedAccountId: req.connectedAccountId,
          result: res,
          isPayoutReady: res?.isPayoutReady,
          onboardingUrl: res?.onboardingUrl,
          notReadyReasons: res?.notReadyReasons,
          requirementsCurrentlyDue: res?.requirementsCurrentlyDue,
          requirementsPastDue: res?.requirementsPastDue,
        });
      } catch {
      }
      this.apReadiness = res || null;
      if (showToast) {
        if (this.apReadiness?.isPayoutReady) {
          this.notification.showSuccessToast('AP payout readiness: Ready');
        } else {
          const msg = this.apReadiness?.message || 'AP payout is not ready. Review requirements.';
          this.notification.showInfoToast(msg);
        }
      }
    } catch (e: any) {
      const msg = e?.error?.Message || e?.message || 'Failed to check AP payout readiness.';
      this.notification.showErrorToast(msg);
    } finally {
      this.apReadinessLoading = false;
    }
  }

  openApOnboarding(url?: string | null): void {
    const link = (url || '').toString().trim();
    if (!link) {
      this.notification.showWarningToast('Onboarding link is not available. Run Check Readiness first.');
      return;
    }
    try {
      window.open(link, '_blank', 'noopener');
    } catch {
      // Fallback toast if window is unavailable in an unusual embedding context
      this.notification.showWarningToast('Unable to open onboarding link in a new tab. Please copy/paste the URL: ' + link);
    }
  }

  // ---- AP Readiness helpers for always-visible badge ----
  getApReadinessLabel(): string {
    if (!this.apReadiness) {
      return 'Unknown';
    }
    return this.apReadiness.isPayoutReady ? 'Ready' : 'Not Ready';
  }

  getApReadinessBadgeClass(): string {
    if (!this.apReadiness) {
      return 'text-secondary-emphasis bg-secondary-subtle border-secondary-subtle';
    }
    return this.apReadiness.isPayoutReady
      ? 'text-success-emphasis bg-success-subtle border-success-subtle'
      : 'text-warning-emphasis bg-warning-subtle border-warning-subtle';
  }

  // ---- Email the AP onboarding (verify) URL using the standard email modal ----
  emailApOnboardingUrl(): void {
    const link = (this.apReadiness?.onboardingUrl || '').toString().trim();
    if (!link) {
      this.notification.showWarningToast('Onboarding link is not available. Run Check Readiness first.');
      return;
    }
    const entityName = (this.selectedEntityDisplayName || '').toString().trim();
    const instructions = 'Please verify your Stripe account by clicking the link below to verify your account.';
    const html = `
      <div style="font-family: Arial, Helvetica, sans-serif; font-size: 13px;">
        <h3 style="margin: 0 0 12px 0;">AP Vendor Stripe Onboarding</h3>
        <div style="margin: 0 0 8px 0;"><strong>Instructions:</strong> ${instructions}</div>
        <div style="margin: 0 0 8px 0;"><strong>Vendor:</strong> ${entityName || '(selected vendor)'}</div>
        <div style="margin: 0 0 8px 0;"><strong>Status:</strong> ${this.getApReadinessLabel()}</div>
        <div style="margin: 0 0 12px 0;"><strong>Verification Link:</strong> <a href="${link}" target="_blank" rel="noopener">${link}</a></div>
        ${this.apReadiness?.notReadyReasons?.length ? `<div><strong>Verification Items:</strong><ul>${this.apReadiness.notReadyReasons.map(r => `<li>${r}</li>`).join('')}</ul></div>` : ''}
      </div>
    `;

    this.emailObject = new EmailModel();
    this.emailObject.Subject = `Stripe Onboarding Link for Vendor ${entityName || ''}`.trim();
    this.emailObject.body = html;
    // this.emailObject.FileName = `AP_Onboarding_${(entityName || 'Vendor').replace(/\s+/g, '_')}.html`;
    // this.emailObject.pdfHtml = html; // allow PDF generation if your modal supports it

    // Open the shared Send Email modal
    try {
      // Flush bindings before opening the modal
      try {
        this.cdr?.detectChanges();
      } catch {
      }
      const modalEl = document.getElementById('sendEmailModal');
      if (modalEl) {
        Modal.getOrCreateInstance(modalEl).show();
      } else {
        this.notification.showInfoToast('Email modal not found in DOM.');
      }
    } catch {
      this.notification.showInfoToast('Unable to open Email modal.');
    }
  }

  // ---- AP Balances (Connect) ----
  async loadApBalance(): Promise<void> {
    try {
      this.apBalanceLoading = true;
      const appEntityId = (this.f['entityId']?.value || this.stripeAdminEntityId || '').toString().trim();
      if (!appEntityId) {
        this.notification.showWarningToast('Select an entity before checking balances.');
        return;
      }
      const connectedAccountId = (this as any)?.stripeSetup?.ap?.connectedAccountId
        || (this as any)?.stripeSetup?.Ap?.connectedAccountId
        || undefined;
      const currency = ((this as any)?.stripeSetup?.ap?.currency || 'usd').toString().toLowerCase();
      const res = await lastValueFrom(this.stripeApService.getBalance({appEntityId, connectedAccountId, currency}));
      try {
        console.log('[Stripe Admin][AP Balance] response', {
          appEntityId,
          connectedAccountId,
          currency,
          result: res,
        });
      } catch {
      }
      this.apBalance = res || {};
    } catch (e: any) {
      const msg = e?.error?.Message || e?.message || 'Failed to fetch AP balances.';
      this.notification.showErrorToast(msg);
    } finally {
      this.apBalanceLoading = false;
    }
  }

  // ---- Transactions helpers (per-row refresh) ----
  getRowPaymentIntentId(row: any): string {
    return (
      row?.StripePaymentIntentId ||
      row?.stripePaymentIntentId ||
      row?.PaymentIntentId ||
      row?.paymentIntentId ||
      row?.Id ||
      row?.id ||
      ''
    );
  }

  cancellingTransferId: string | null = null;

  async onCancelTransfer(row: any): Promise<void> {
    const transferId = (row?.StripeTransferId ?? row?.stripeTransferId ?? '').toString().trim();
    const appEntityId = (this.f['entityId']?.value || this.stripeAdminEntityId || '').toString().trim();
    if (!transferId || !appEntityId) {
      this.notification.showWarningToast('Missing Transfer ID or Entity ID for cancellation.');
      return;
    }
    this.cancellingTransferId = transferId;
    try {
      const result = await lastValueFrom(this.stripeApService.reverseTransfer({
        appEntityId,
        transferId,
        reason: 'Cancelled by user',
      }));
      if (result?.blocked) {
        this.notification.showErrorToast(result.message || 'Transfer cannot be reversed.');
        return;
      }
      if (result?.reversed) {
        row.Status = result.status || 'Canceled';
        row.status = result.status || 'Canceled';
        row.IsCancellable = false;
        row.isCancellable = false;
        this.notification.showSuccessToast(result.message || 'Transfer successfully reversed.');
      } else {
        this.notification.showWarningToast(result?.message || 'Reversal did not complete.');
      }
    } catch (e: any) {
      const msg = e?.error?.Message || e?.error?.message || e?.message || 'Failed to cancel transfer.';
      this.notification.showErrorToast(msg);
    } finally {
      this.cancellingTransferId = null;
    }
  }

  onRefreshTransactionStatus(row: any): void {
    try {
      const pi = String(this.getRowPaymentIntentId(row) || '').trim();
      if (!pi) {
        this.notification.showWarningToast('Missing Payment Intent Id for this row.');
        return;
      }

      this.transactionsRowLoading[pi] = true;
      this.stripePaymentService.getPaymentStatus(pi).subscribe({
        next: (res: any) => {
          // Service unwraps to a DTO; prefer res.Status/res.status
          const status = res?.Status || res?.status || '';
          const updated = res?.UpdatedUtc || res?.updatedUtc || new Date().toISOString();
          if (status) {
            row.Status = status;
            row.status = status;
          }
          if (updated) {
            row.UpdatedUtc = updated;
            row.updatedUtc = updated;
          }
          this.notification.showSuccessToast('Transaction status refreshed.');
        },
        error: (err: any) => {
          const msg = err?.error?.Message || err?.message || 'Failed to refresh status.';
          this.notification.showWarningToast(msg);
        },
        complete: () => {
          this.transactionsRowLoading[pi] = false;
        }
      });
    } catch (e) {
      this.notification.showWarningToast('Unable to refresh status for this row.');
    }
  }

  getAchSetupTraceLabel(): string {
    switch (this.achSetupTraceStage) {
      case 1:
        return 'ACH Setup: Step 1/3 – Ensuring Stripe customer…';
      case 2:
        return 'ACH Setup: Step 2/3 – Creating SetupIntent…';
      case 3:
        return 'ACH Setup: Step 3/3 – Refreshing setup status…';
      case 4:
        return 'ACH Setup: Launched.';
      default:
        return '';
    }
  }

  // Removed complex post-verification scheduling in favor of simple, linear
  // await/refresh patterns executed in finally blocks of the verification flows.

  getAchSetupTraceBadgeClass(): string {
    // Keep it subtle; use info during steps, success when done
    return this.achSetupTraceStage === 4 ? 'bg-success' : 'bg-info';
  }

  ngOnInit() {
    this.titleService.setTitle($localize`:Stripe Setup@@stripeSetup:Stripe Setup`);
    this.loadEntityLookup();
    this.loadBankLookup();
    // Initialize Transactions date range to last 30 days on form load
    this.setDefaultTransactionsDateRangeLast30();
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: any) => {
      const routeId = (params?.id || '').toString().trim();
      this.applyRouteId(routeId);
      this.applyFlowScope((this.route.snapshot.queryParamMap.get('flow') || '').toString().trim());
      this.loadData();

      if (this.stripeAdminID) {
        this.buttonConfig.reset = false;
        this.buttonConfig.delete = true;
        return;
      }

      this.buttonConfig.delete = false;
    });

    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((queryParams: any) => {
      const flow = (queryParams?.flow || '').toString().trim();
      this.applyFlowScope(flow);
      const entityId = (queryParams?.entityId || '').toString().trim();
      this.applyQueryEntityScope(entityId);
    });

    // After initial route/query param application, auto-check AP readiness for existing setups
    try {
      setTimeout(() => {
        try {
          const flowKey = this.getActiveFlowKey();
          const appEntityId = (this.f['entityId']?.value || this.stripeAdminEntityId || '').toString().trim();
          const shouldAuto = flowKey === 'ap' && !!appEntityId && !this.isNewSetupMode;
          if (shouldAuto) {
            try {
              console.log('[Stripe Admin][AP Readiness] auto-check on init', {flow: flowKey, appEntityId});
            } catch {
            }
            this.checkApReadiness(false).then();
          }
        } catch {
        }
      }, 0);
    } catch {
    }
  }

  private setDefaultTransactionsDateRangeLast30(): void {
    try {
      const today = new Date();
      const to = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const from = new Date(to);
      from.setDate(from.getDate() - 30);
      const fmt = (d: Date) => `${d.getFullYear().toString().padStart(4, '0')}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
      // Only set defaults if not already populated (e.g., by query params)
      if (!this.transactionsFromDate) {
        this.transactionsFromDate = fmt(from);
      }
      if (!this.transactionsToDate) {
        this.transactionsToDate = fmt(to);
      }
    } catch {
      // noop
    }
  }

  get isFlowScoped(): boolean {
    return this.getActiveFlowKey() !== null;
  }

  isFlowSectionVisible(flow: 'ar' | 'ap'): boolean {
    const activeFlow = this.getActiveFlowKey();
    return activeFlow === flow;
  }

  private getActiveFlowKey(): 'ar' | 'ap' | null {
    const flow = Number(this.f['entityType']?.value);
    if (flow === 1) {
      return 'ar';
    }

    if (flow === 2) {
      return 'ap';
    }

    return null;
  }

  private getFlowType(flow: 'ar' | 'ap'): 1 | 2 {
    return flow === 'ar' ? 1 : 2;
  }

  private applyRouteId(routeIdRaw: string): void {
    const routeId = (routeIdRaw || '').toString().trim();
    const [, entityId] = routeId.split('__');
    this.isNewSetupMode = !routeId;
    this.stripeAdminEntityId = (entityId || routeId).toString().trim();
    this.stripeAdminID = this.stripeAdminEntityId;
  }

  private applyFlowScope(flowRaw: string): void {
    const normalizedFlow = (flowRaw || '').toString().trim().toUpperCase();
    if (normalizedFlow === 'AR' || normalizedFlow === 'AP') {
      this.scopedFlow = normalizedFlow;
      this.f['entityType']?.setValue(normalizedFlow === 'AR' ? '1' : '2', {emitEvent: false});
      // Normalize the selected transactions status to a valid option for the active scope
      const options = this.getTransactionStatusOptions();
      if (!options.includes(this.transactionsStatus)) {
        this.transactionsStatus = 'All';
      }
      return;
    }

    this.scopedFlow = '';
  }

  private applyQueryEntityScope(entityIdRaw: string): void {
    const entityId = (entityIdRaw || '').toString().trim();
    this.queryParamEntityId = entityId;

    if (!entityId) {
      this.syncVerifiedGlobalControlLock();
      return;
    }

    this.stripeAdminEntityId = entityId;
    this.stripeAdminID = entityId;
    this.f['entityId']?.setValue(entityId, {emitEvent: false});
    this.syncVerifiedGlobalControlLock();
  }

  showSetupTab(): void {
    this.activeTab = 'setup';
  }

  showTransactionsTab(): void {
    this.activeTab = 'transactions';
    this.transactionsPage = 1;
    // Ensure current status selection is valid for the mode when opening the tab
    const options = this.getTransactionStatusOptions();
    if (!options.includes(this.transactionsStatus)) {
      this.transactionsStatus = 'All';
    }
    this.loadTransactions().then();
  }

  get f() {
    return this.stripeAdminForm.controls;
  }

  get canLaunchAchSetup(): boolean {
    const entityId = (this.f['entityId']?.value || this.stripeAdminEntityId || '').toString().trim();
    const selectedEntity = this.resolveEntityById(entityId);
    const entityBankId = this.resolveEntityBankId(selectedEntity);
    const appBankId = (this.f['bankId']?.value || '').toString().trim();
    const bankName = (this.f['bankName']?.value || '').toString().trim();

    if (!entityId || !entityBankId || !appBankId || !bankName || this.isSelectedFlowLockedByVerification()) {
      return false;
    }

    return appBankId === entityBankId;
  }

  get selectedEntityDisplayName(): string {
    const selectedEntityId = (this.f['entityId']?.value || this.stripeAdminEntityId || '').toString().trim();
    if (selectedEntityId) {
      const selectedEntity = (this.entityOptions || []).find((entity: any) =>
        (entity?.id || entity?.Id || '').toString().trim() === selectedEntityId
      );
      const selectedEntityName = (selectedEntity?.name || selectedEntity?.Name || '').toString().trim();
      if (selectedEntityName) {
        return selectedEntityName;
      }
    }

    const entityName = (this.stripeAdminEntityName || '').toString().trim();
    if (entityName) {
      return entityName;
    }

    const entityId = (this.f['entityId']?.value || this.stripeAdminEntityId || '').toString().trim();
    if (entityId) {
      return entityId;
    }

    return 'Selected entity';
  }

  get selectedTransactionsCount(): number {
    return Object.keys(this.selectedTransactions).filter((key) => this.selectedTransactions[key]).length;
  }

  // Return the appropriate status options based on current scoped flow
  getTransactionStatusOptions(): string[] {
    return this.scopedFlow === 'AP' ? this.apTransactionStatuses : this.arTransactionStatuses;
  }

  createForm() {
    this.stripeAdminForm = this.formBuilder.group({
      bankId: new UntypedFormControl(''),
      bankName: new UntypedFormControl(''),
      entityId: new UntypedFormControl(''),
      entityType: new UntypedFormControl(''),
      achAccountHolderName: new UntypedFormControl(''),
      achEmail: new UntypedFormControl(''),
      achRoutingNumber: new UntypedFormControl(''),
      achAccountNumber: new UntypedFormControl(''),
      achAccountHolderType: new UntypedFormControl('individual'),
    });
  }

  private loadEntityLookup(): void {
    this.table.getData('entity', '?state=active&pageNumber=0&pageSize=999999').subscribe({
      next: (result: any) => {
        const rows = result?.Data ?? [];
        this.entityOptions = Array.isArray(rows) ? rows : [];
        const currentEntityId = (this.f['entityId']?.value || this.stripeAdminEntityId || '').toString().trim();
        if (currentEntityId) {
          const selectedEntity = this.resolveEntityById(currentEntityId);
          this.applyEntitySelection(selectedEntity);
          const entityBankId = this.resolveEntityBankId(selectedEntity);
          this.loadBankById(entityBankId);
        }
      },
      error: () => {
        this.entityOptions = [];
      }
    });
  }

  private loadBankLookup(): void {
    this.table.getData('bank', '?state=active&pageNumber=0&pageSize=999999').subscribe({
      next: (result: any) => {
        const rows = result?.Data ?? [];
        this.bankOptions = Array.isArray(rows) ? rows : [];
      },
      error: () => {
        this.bankOptions = [];
      }
    });
  }

  private resolveEntityById(entityId: string): any {
    const normalized = (entityId ?? '').toString().trim();
    if (!normalized) {
      return null;
    }

    return (this.entityOptions || []).find((entity: any) =>
      (entity?.id ?? '').toString().trim() === normalized
    ) || null;
  }

  private resolveEntityBankId(entity: any): string {
    return (entity?.bank_id ?? '').toString().trim();
  }

  private loadBankById(bankId: string): void {
    const normalizedBankId = (bankId ?? '').toString().trim();
    if (!normalizedBankId) {
      return;
    }

    this.table.getData('bank', normalizedBankId).subscribe({
      next: (bankResult: any) => {
        const bankRecord = Array.isArray(bankResult?.Data) ? bankResult.Data[0] : (bankResult?.Data ?? bankResult);
        const bankName = (bankRecord?.name ?? '').toString().trim();

        this.stripeAdminForm.patchValue({
          bankId: normalizedBankId,
          bankName,
        });

        this.stripeGlobal = {
          ...this.stripeGlobal,
          appBankId: normalizedBankId,
          bankDisplayName: bankName,
        };
      },
      error: () => {
        this.stripeAdminForm.patchValue({
          bankId: normalizedBankId,
          bankName: '',
        });
      }
    });
  }

  // private resolveBankNameFromSetupData(data: any, appBankId: string): string {
  //   const normalizedAppBankId = (appBankId || '').toString().trim();
  //   if (!normalizedAppBankId) {
  //     return '';
  //   }
  //
  //   return (data?.BankName || '').toString().trim();
  // }

  onEntitySelectionChange(): void {
    const entityId = (this.f['entityId']?.value || '').toString().trim();
    const entity = this.resolveEntityById(entityId);
    this.applyEntitySelection(entity, true);

    const entityBankId = this.resolveEntityBankId(entity);
    this.loadBankById(entityBankId);
    console.log('Entity selection changed', {entityId, entity, entityBankId});
  }

  private applyEntitySelection(entity: any, clearBankName: boolean = false): void {
    const entityId = (entity?.id || entity?.Id || '').toString().trim();
    const entityName = (entity?.name || entity?.Name || '').toString().trim();
    const entityCompanyId = (entity?.company_id || '').toString().trim();
    const entityBankId = this.resolveEntityBankId(entity);

    if (entityId) {
      this.stripeAdminEntityId = entityId;
    }
    if (entityName) {
      this.stripeAdminEntityName = entityName;
    }

    this.stripeAdminForm.patchValue({
      entityId,
      bankId: entityBankId,
      bankName: clearBankName ? '' : (this.f['bankName']?.value || '').toString().trim(),
    });

    if (entityId) {
      this.stripeAdminID = entityId;
    }

    this.stripeGlobal = {
      ...this.stripeGlobal,
      appEntityId: entityId,
      companyId: entityCompanyId,
    };
  }

  onTransactionsPageChange(page: number): void {
    this.transactionsPage = page;
    this.loadTransactions().then();
  }

  onTransactionsPageSizeChange(pageSize: number): void {
    this.transactionsPageSize = Number(pageSize) || 20;
    this.transactionsPage = 1;
    this.loadTransactions().then();
  }

  onApplyTransactionsDateRange(): void {
    this.transactionsPage = 1;
    this.loadTransactions().then();
  }

  onResetTransactionsDateRange(): void {
    this.transactionsFromDate = null;
    this.transactionsToDate = null;
    this.transactionsStatus = 'All';
    this.transactionsPage = 1;
    this.loadTransactions().then();
  }

  isTransactionSelected(row: any): boolean {
    const key = this.getTransactionsRowKey(row);
    return key ? Boolean(this.selectedTransactions[key]) : false;
  }

  getTransactionStatusBadgeClass(status: any): string {
    const normalized = (status ?? '').toString().trim().toLowerCase();

    if (normalized === 'succeeded') {
      return 'bg-success-subtle text-success border border-success-subtle';
    }

    if (normalized === 'failed' || normalized === 'canceled') {
      return 'bg-danger-subtle text-danger border border-danger-subtle';
    }

    if (normalized === 'requiresaction' || normalized === 'processing' || normalized === 'pending') {
      return 'bg-warning-subtle text-warning-emphasis border border-warning-subtle';
    }

    if (normalized === 'refunded') {
      return 'bg-info-subtle text-info-emphasis border border-info-subtle';
    }

    return 'bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle';
  }

  // Build datatable columns; include extra Stripe IDs in AP mode only
  // getTransactionsColumns(cancellableCell: any, statusCell: any): any[] {
  //   const cols: any[] = [
  //     { header: 'App Bank', field: 'AppBankName', orderable: true },
  //     { header: 'Direction', field: 'Direction', orderable: true },
  //     { header: 'Flow Type', field: 'FlowType', orderable: true },
  //     { header: 'Amount', field: 'Amount', orderable: true, pipe: 'decimal' },
  //     { header: 'Stripe Fee', field: 'StripeFeeAmount', orderable: true, pipe: 'decimal' },
  //     { header: 'Is Cancellable', field: 'IsCancellable', orderable: true, template: cancellableCell },
  //     { header: 'Status', field: 'Status', orderable: true, template: statusCell },
  //   ];
  //
  //   if (this.scopedFlow === 'AP') {
  //     cols.push(
  //       { header: 'Stripe Payout Id', field: 'StripePayoutId', orderable: true },
  //       { header: 'Stripe Transfer Id', field: 'StripeTransferId', orderable: true },
  //     );
  //   }
  //
  //   cols.push(
  //     { header: 'Last Update', field: 'UpdatedUtc', orderable: true, pipe: 'date', dateFormat: 'MM/dd/yyyy hh:mm a' },
  //     { header: 'Created', field: 'CreatedUtc', orderable: true, pipe: 'date', dateFormat: 'MM/dd/yyyy hh:mm a' },
  //   );
  //
  //   return cols;
  // }

  // Expose columns as a getter to avoid calling methods directly from template contexts
  get transactionsColumns(): any[] {
    const cols: any[] = [
      {header: 'App Bank', field: 'AppBankName', orderable: true},
      {header: 'Direction', field: 'Direction', orderable: true},
      {header: 'Stripe Flow ', field: 'StripeFlowType', orderable: true},
      {header: 'Amount', field: 'Amount', orderable: true, pipe: 'decimal'},
      {header: 'Stripe Fee', field: 'StripeFeeAmount', orderable: true, pipe: 'decimal'},
      {header: 'Is Cancellable', field: 'IsCancellable', orderable: true, template: this.cancellableCellTpl},
      {header: 'Status', field: 'Status', orderable: true, template: this.statusCellTpl},
      {header: 'Missing App Trans', field: 'IsMissingLocalTransaction', orderable: true, template: this.missingCellTpl},
    ];

    if (this.scopedFlow === 'AP') {
      cols.push(
        {header: 'Stripe Payment Id', field: 'StripeConnectPaymentId', orderable: true},
        {header: 'Stripe Transfer Id', field: 'StripeTransferId', orderable: true, template: this.stripeTransferCellTpl},
      );
    } else {
      cols.push(
        {header: 'Payment Intent Id', field: 'StripePaymentIntentId', orderable: true, template: this.paymentIntentCellTpl},
      );
    }

    cols.push(
      {header: 'Last Update', field: 'UpdatedUtc', orderable: true, pipe: 'date', dateFormat: 'MM/dd/yyyy hh:mm a'},
      {header: 'Created', field: 'CreatedUtc', orderable: true, pipe: 'date', dateFormat: 'MM/dd/yyyy hh:mm a'},
    );

    return cols;
  }


  onTransactionsRowSelectionChange(row: any, isSelected: boolean): void {
    const key = this.getTransactionsRowKey(row);
    if (!key) {
      return;
    }

    if (isSelected) {
      this.selectedTransactions[key] = true;
    } else {
      delete this.selectedTransactions[key];
    }
  }

  private getTransactionsRowKey(row: any): string {
    return (
      row?.id ||
      row?.Id ||
      row?.stripePaymentIntentId ||
      row?.StripePaymentIntentId ||
      row?.paymentIntentId ||
      row?.PaymentIntentId ||
      ''
    ).toString().trim();
  }

  private resolveEffectiveAppBankId(source?: any): string {
    const fromForm = (this.f['bankId']?.value || '').toString().trim();
    if (fromForm) {
      return fromForm;
    }

    return (source?.AppBankId ?? '').toString().trim();
  }

  private resolveFriendlyEntityName(source?: any): string {
    return (source?.EntityName ?? '').toString().trim();
  }

  private async loadTransactions(): Promise<void> {
    const appBankId = this.resolveEffectiveAppBankId();
    const appEntityId = (this.f['entityId']?.value || this.stripeAdminEntityId || '').toString().trim();
    const stripeFlowTypeRaw = this.f['entityType']?.value;
    const stripeFlowType = stripeFlowTypeRaw === '' || stripeFlowTypeRaw === null || stripeFlowTypeRaw === undefined
      ? undefined
      : Number(stripeFlowTypeRaw);
    const status = (this.transactionsStatus || 'All').toString().trim() || 'All';
    const fromDate = this.normalizeDateFilter(this.transactionsFromDate);
    const toDate = this.normalizeDateFilter(this.transactionsToDate);


    // In AP mode, list transactions by entity only using /api/stripe/payments/all?appEntityId=...
    const isApMode = this.scopedFlow === 'AP';
    if (!appEntityId || (!isApMode && !appBankId)) {
      this.transactions = [];
      this.transactionsTotalRecords = 0;
      this.transactionsStartIndex = 0;
      this.transactionsEndIndex = 0;
      return;
    }

    this.transactionsLoading = true;
    try {
      // AR vs AP branching:
      // - AP: paged entity-scoped listing via /api/stripe/payments/all (with stripeFlowType=2)
      // - AR: recent transactions via /api/stripe/transactions using take=pageSize
      const response: any = isApMode
        ? await lastValueFrom(this.stripePaymentService.getAllPaymentsByEntity(
          appEntityId,
          this.transactionsPage,
          this.transactionsPageSize,
          fromDate,
          toDate,
          status,
        ))
        : await lastValueFrom(this.stripePaymentService.getTransactions(
          appEntityId,
          // Default AR flow type to 1 if not explicitly set
          (stripeFlowType === undefined || stripeFlowType === null || String(stripeFlowType).trim() === '') ? 1 : stripeFlowType,
          this.transactionsPage,
          this.transactionsPageSize,
          status,
          fromDate,
          toDate,
        ));
      // Debug log: full API response for transactions
      console.log('[Stripe Admin][Transactions] API response:', response);

      // Normalize rows — both AR and AP now return paged responses
      const rows: any[] = response?.data || response?.Data || [];
      // Debug log: normalized rows + paging
      console.log('[Stripe Admin][Transactions] normalized rows count:', Array.isArray(rows) ? rows.length : 0,
        'page:', (response?.page ?? response?.Page), 'pageSize:', (response?.pageSize ?? response?.PageSize),
        'totalCount:', (response?.totalCount ?? response?.TotalCount));

      // If AP mode, adjust row shape to align with datatable expectations (PascalCase fields)
      if (isApMode && Array.isArray(rows)) {
        this.transactions = rows.map((r: any) => {
          // Preserve original fields and add PascalCase projections used by the grid
          return {
            ...r,
            AppBankName: r?.AppBankName ?? r?.appBankName ?? '',
            Direction: r?.Direction ?? r?.direction ?? '',
            FlowType: r?.FlowType ?? r?.flowType ?? '',
            Amount: Number(r?.Amount ?? r?.amount ?? 0),
            StripeFeeAmount: r?.StripeFeeAmount ?? r?.stripeFeeAmount, // may be undefined for AP payouts
            IsCancellable: (r?.IsCancellable ?? r?.isCancellable) ?? false,
            Status: r?.Status ?? r?.status ?? '',
            StripePayoutId: r?.StripePayoutId ?? r?.stripePayoutId ?? r?.stripePayoutID ?? r?.stripePayout ?? null,
            StripeTransferId: r?.StripeTransferId ?? r?.stripeTransferId ?? r?.stripeTransferID ?? r?.stripeTransfer ?? null,
            AppT_headerId: r?.AppT_headerId ?? r?.appT_headerId ?? r?.AppTHeaderId ?? r?.appTHeaderId ?? null,
            CreatedUtc: r?.CreatedUtc ?? r?.createdUtc ?? r?.createdAtUtc ?? null,
            UpdatedUtc: r?.UpdatedUtc ?? r?.updatedUtc ?? r?.updatedAtUtc ?? null,
          };
        });
      } else {
        this.transactions = Array.isArray(rows) ? rows.map((r: any) => ({
          ...r,
          StripePaymentIntentId: r?.StripePaymentIntentId ?? r?.stripePaymentIntentId ?? null,
          AppT_headerId: r?.AppT_headerId ?? r?.appT_headerId ?? r?.AppTHeaderId ?? r?.appTHeaderId ?? null,
        })) : [];
      }
      // Both AR and AP now return paged responses
      this.transactionsTotalRecords = Number(
        (response?.totalCount ?? response?.TotalCount) ?? this.transactions.length ?? 0
      );

      const currentPage = Number(response?.page ?? response?.Page ?? this.transactionsPage);
      const currentPageSize = Number(response?.pageSize ?? response?.PageSize ?? this.transactionsPageSize);
      if (!Number.isNaN(currentPage) && currentPage > 0) {
        this.transactionsPage = currentPage;
      }
      if (!Number.isNaN(currentPageSize) && currentPageSize > 0) {
        this.transactionsPageSize = currentPageSize;
      }

      if (this.transactionsTotalRecords > 0 && this.transactions.length > 0) {
        this.transactionsStartIndex = ((this.transactionsPage - 1) * this.transactionsPageSize) + 1;
        this.transactionsEndIndex = this.transactionsStartIndex + this.transactions.length - 1;
      } else {
        this.transactionsStartIndex = 0;
        this.transactionsEndIndex = 0;
      }
    } catch (error: any) {
      this.transactions = [];
      this.transactionsTotalRecords = 0;
      this.transactionsStartIndex = 0;
      this.transactionsEndIndex = 0;
      const apiError = error?.error?.Message || error?.error?.message || error?.message || 'Failed to load Stripe transactions.';
      this.notification.showErrorToast(apiError);
    } finally {
      this.transactionsLoading = false;
    }
  }

  private normalizeDateFilter(value: any): string | undefined {
    if (value && typeof value === 'object' && Number.isFinite(value.year) && Number.isFinite(value.month) && Number.isFinite(value.day)) {
      const year = Number(value.year);
      const month = Number(value.month);
      const day = Number(value.day);
      return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    }

    const raw = (value ?? '').toString().trim();
    if (!raw) {
      return undefined;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      return raw;
    }

    const parsedDate = new Date(raw);
    if (Number.isNaN(parsedDate.getTime())) {
      return undefined;
    }

    return parsedDate.toISOString().slice(0, 10);
  }

  private createDefaultStripeFlow() {
    return {
      enabled: false,
      isActive: false,
      stripePaymentMethodId: '',
      stripeCustomerId: '',
      methodType: '',
      bankName: '',
      last4: '',
      achStatus: '',
      hostedVerificationUrl: '',
      setupIntentId: '',
      setupIntentStatus: '',
      setupIntentClientSecret: '',
      verifiedAtUtc: null,
    };
  }

  private createDefaultStripeGlobal() {
    return {
      id: '',
      bankId: '',
      appBankId: '',
      appEntityId: '',
      companyId: '',
      bankDisplayName: '',
      stripeBankName: '',
      bankLast4: '',
      bankAccountType: '',
      bankAccountHolderType: '',
      bankFingerprint: '',
      bankNetworkPreferred: '',
      stripePaymentMethodType: '',
      isReusableForFuturePayments: true,
      createdUtc: null,
      updatedUtc: null,
    };
  }

  // mappers moved to helpers (mapStripeFlowDto/mapStripeGlobalDto)

  private getFlowAchStatus(flow: 'ar' | 'ap'): string {
    const setupFlow = this.getSetupFlow(flow);
    const gate = this.getVerificationGateState(flow);
    const view = computeAchStatusView(setupFlow, {showInstant: gate.showInstant, showMicrodeposit: gate.showMicrodeposit});
    return view.label;
  }


  hasFlowAchStatus(flow: 'ar' | 'ap'): boolean {
    return Boolean(this.getSetupFlow(flow));
  }

  getFlowAchStatusClass(flow: 'ar' | 'ap'): string {
    const setupFlow = this.getSetupFlow(flow);
    const gate = this.getVerificationGateState(flow);
    const view = computeAchStatusView(setupFlow, {showInstant: gate.showInstant, showMicrodeposit: gate.showMicrodeposit});
    return view.badgeClass;
  }

  isFlowAchVerified(flow: 'ar' | 'ap'): boolean {
    // STRICT: Only treat as verified when AchStatus is exactly 'Verified'.
    const setupFlow = this.getSetupFlow(flow);
    if (!setupFlow) {
      return false;
    }
    const achStatusExact = (setupFlow.achStatus ?? '').toString().trim();
    return achStatusExact === 'Verified';
  }

  isFlowLockedByVerification(flow: 'ar' | 'ap'): boolean {
    return this.isFlowAchVerified(flow);
  }

  isGlobalLockedByVerification(): boolean {
    return this.isFlowLockedByVerification('ar') || this.isFlowLockedByVerification('ap');
  }

  isSelectedFlowLockedByVerification(): boolean {
    const activeFlow = this.getActiveFlowKey();
    if (!activeFlow) {
      return false;
    }

    return this.isFlowLockedByVerification(activeFlow);
  }

  private syncVerifiedGlobalControlLock(): void {
    const shouldLock = this.isGlobalLockedByVerification() || !!this.queryParamEntityId;
    const entityIdControl = this.f['entityId'];
    const entityTypeControl = this.f['entityType'];

    if (shouldLock) {
      if (entityIdControl?.enabled) {
        entityIdControl.disable({emitEvent: false});
      }
      if (entityTypeControl?.enabled) {
        entityTypeControl.disable({emitEvent: false});
      }
      return;
    }

    if (entityIdControl?.disabled) {
      entityIdControl.enable({emitEvent: false});
    }
    if (entityTypeControl?.disabled) {
      entityTypeControl.enable({emitEvent: false});
    }
  }

  private getSetupFlow(flow: 'ar' | 'ap') {
    return flow === 'ar' ? this.stripeSetup?.ar : this.stripeSetup?.ap;
  }

  private getFlowVerificationState(flow: 'ar' | 'ap'): {
    isLocked: boolean;
    hasHostedVerificationUrl: boolean;
    hasClientSecret: boolean;
    hasSetupIntentId: boolean;
    requiresMicrodepositVerification: boolean;
  } {
    const setupFlow = this.getSetupFlow(flow);
    const hostedVerificationUrl = (setupFlow?.hostedVerificationUrl ?? '').toString().trim();
    const setupIntentClientSecret = (setupFlow?.setupIntentClientSecret ?? '').toString().trim();
    const setupIntentId = (setupFlow?.setupIntentId ?? '').toString().trim();
    return {
      isLocked: this.isFlowLockedByVerification(flow),
      hasHostedVerificationUrl: Boolean(hostedVerificationUrl),
      hasClientSecret: Boolean(setupIntentClientSecret),
      hasSetupIntentId: Boolean(setupIntentId),
      requiresMicrodepositVerification: this.requiresFlowMicrodepositVerification(flow),
    };
  }

  getVerificationGateState(flow: 'ar' | 'ap'): {
    showInstant: boolean;
    showMicrodeposit: boolean;
    showUnavailable: boolean;
    hasHostedVerificationUrl: boolean;
  } {
    const flowNode = this.getSetupFlow(flow);
    const runtimeHostedVerificationUrl = (this.pendingVerificationUrl ?? '').toString().trim();
    const persistedHostedVerificationUrl = (flowNode?.hostedVerificationUrl ?? '').toString().trim();
    const hostedVerificationUrl = runtimeHostedVerificationUrl || persistedHostedVerificationUrl;
    const runtimeClientSecret = (this.pendingSetupClientSecret ?? '').toString().trim();
    const persistedClientSecret = (flowNode?.setupIntentClientSecret ?? '').toString().trim();
    const setupIntentClientSecret = runtimeClientSecret || persistedClientSecret;
    const achStatusExact = (flowNode?.achStatus ?? '').toString().trim();
    const isVerified = achStatusExact === 'Verified';
    const hasHostedVerificationUrl = Boolean(hostedVerificationUrl);
    const hasClientSecret = Boolean(setupIntentClientSecret);
    const showInstant = hasClientSecret && !isVerified;
    // Allow microdeposit after instant-verify failure for the given flow,
    // even if a hosted URL is not present (parity across AR/AP).
    const showMicrodeposit = !isVerified && (hasHostedVerificationUrl || this.instantVerifyFailed[flow]);
    const showUnavailable = !isVerified && !showInstant && !showMicrodeposit;

    return {
      showInstant,
      showMicrodeposit,
      showUnavailable,
      hasHostedVerificationUrl,
    };
  }

  private requiresFlowMicrodepositVerification(flow: 'ar' | 'ap'): boolean {
    const setupFlow = this.getSetupFlow(flow);
    if (!setupFlow || this.isFlowAchVerified(flow)) {
      return false;
    }

    const achStatus = normalizeAchStatusValue(setupFlow.achStatus);
    const setupStatus = normalizeAchStatusValue(setupFlow.setupIntentStatus);
    return isStatusPendingLike(achStatus) || isStatusPendingLike(setupStatus);
  }


  shouldShowFlowActivationUpdateButton(flow: 'ar' | 'ap'): boolean {
    return this.isFlowLockedByVerification(flow);
  }

  getFlowCardColumnClass(): string {
    return this.isFlowScoped ? 'col-md-12' : 'col-md-6';
  }

  getFlowAchStatusLabel(flow: 'ar' | 'ap'): string {
    const setupFlow = this.getSetupFlow(flow);
    if (!setupFlow) {
      return 'Requires setup';
    }

    if (this.isFlowAchVerified(flow)) {
      return 'Verified';
    }

    return this.getFlowAchStatus(flow);
  }

  isAnySetupActionLoading(): boolean {
    return this.stripeAchSetupLoading || this.stripeAutoConfigLoading || this.stripeAchConfirmLoading;
  }

  // Ensure micro-deposit actions are only shown when a real SetupIntent exists
  hasFlowSetupIntent(flow: 'ar' | 'ap'): boolean {
    const setupFlow = this.getSetupFlow(flow);
    const id = (setupFlow?.setupIntentId ?? '').toString().trim();
    return !!id && id.startsWith('seti_');
  }

  isLaunchAchSetupDisabled(): boolean {
    // Revert to original, simpler rule so AP launch isn’t blocked by unrelated locks:
    // Disable only while loading or when launch prerequisites aren’t met.
    return this.isAnySetupActionLoading() || !this.canLaunchAchSetup;
  }

  isAutoAssignPaymentMethodsDisabled(): boolean {
    return this.isAnySetupActionLoading();
  }

  isPendingVerificationContinueDisabled(): boolean {
    return this.stripeAchConfirmLoading || (!this.pendingVerificationUrl && !this.pendingSetupElementReady);
  }

  isFlowActivationUpdateDisabled(flow: 'ar' | 'ap'): boolean {
    return this.isFlowActivationUpdateLoading(flow) || this.isAnySetupActionLoading();
  }

  isFlowDetailsLocked(flow: 'ar' | 'ap'): boolean {
    return this.isFlowLockedByVerification(flow);
  }


  openStripeInstantVerifyModal(flow: 'ar' | 'ap'): void {
    // Mirror AR flow for AP: use the same single entry point and modal.
    // Guard against duplicate opens (rapid clicks or duplicate bindings)
    if (this.stripeInstantVerifyDialogOpen || this.verifyInFlight[flow]) {
      return;
    }
    this.verifyInFlight[flow] = true;
    const setupFlow = this.getSetupFlow(flow);
    // Reset failure flag on (re)open
    this.instantVerifyFailed[flow] = false;
    this.pendingSetupFlow = flow;
    const persistedClientSecret = (setupFlow?.setupIntentClientSecret ?? '').toString().trim();
    const persistedHostedVerificationUrl = (setupFlow?.hostedVerificationUrl ?? '').toString().trim();

    if (!this.pendingSetupClientSecret) {
      this.pendingSetupClientSecret = persistedClientSecret;
    }

    if (!this.pendingVerificationUrl) {
      this.pendingVerificationUrl = persistedHostedVerificationUrl;
    }

    // Trace: before opening modal, show what we will use
    try {
      console.log('[Stripe Setup] Open verification modal', {
        flow,
        persistedClientSecret,
        persistedHostedVerificationUrl,
        pendingSetupClientSecret: this.pendingSetupClientSecret,
        pendingVerificationUrl: this.pendingVerificationUrl,
      });
    } catch {
    }

    this.openStripeInstantVerifyDialog();
  }

  canVerifyFlowMicrodeposits(): boolean {
    const appEntityId = (this.f['entityId']?.value || this.stripeAdminEntityId || '').toString().trim();
    if (!appEntityId) {
      return false;
    }

    if (this.isAmountVerificationMode()) {
      const amount1 = this.parseMicrodepositAmount(this.microdepositAmount1);
      const amount2 = this.parseMicrodepositAmount(this.microdepositAmount2);
      return amount1 !== null && amount2 !== null;
    }

    return Boolean((this.microdepositDescriptorCode || '').toString().trim());
  }

  shouldShowFlowMicrodepositVerificationAction(flow: 'ar' | 'ap'): boolean {
    if (!this.hasFlowSetupIntent(flow)) {
      return false;
    }
    const state = this.getFlowVerificationState(flow);
    const hasInstantPath = state.hasClientSecret;
    return !state.isLocked
      && (state.requiresMicrodepositVerification
        || (!hasInstantPath && (state.hasSetupIntentId || state.hasHostedVerificationUrl)));
  }

  isFlowMicrodepositLaunchDisabled(flow: 'ar' | 'ap'): boolean {
    return this.isFlowLockedByVerification(flow) || this.isAnySetupActionLoading() || !this.hasFlowSetupIntent(flow);
  }

  isFlowMicrodepositSubmitDisabled(): boolean {
    return this.stripeAchConfirmLoading || !this.canVerifyFlowMicrodeposits();
  }

  isAmountVerificationMode(): boolean {
    return this.microdepositVerifyMode === 'amounts';
  }

  openFlowMicrodepositVerifyModal(flow: 'ar' | 'ap'): void {
    if (this.isFlowMicrodepositLaunchDisabled(flow)) {
      return;
    }

    this.microdepositVerifyFlow = flow;
    this.microdepositVerifyModalOpen = true;
    if (!this.microdepositVerifyMode) {
      this.microdepositVerifyMode = 'amounts';
    }
  }

  closeFlowMicrodepositVerifyModal(): void {
    this.microdepositVerifyModalOpen = false;
    // Auto-refresh AR status after closing app-coded microdeposit modal
    try {
      if ((this.microdepositVerifyFlow || this.getActiveFlowKey()) === 'ar') {
        this.refreshSetupStatus(false).then();
      }
    } catch {
      // ignore
    }
  }

  setMicrodepositVerifyMode(mode: 'amounts' | 'descriptor'): void {
    this.microdepositVerifyMode = mode;
  }

  getMicrodepositVerificationSetupIntentId(): string {
    return (this.getSetupFlow(this.microdepositVerifyFlow)?.setupIntentId || '').toString().trim();
  }

  getMicrodepositVerificationFlowLabel(): string {
    return this.microdepositVerifyFlow === 'ar' ? 'AR' : 'AP';
  }

  updateFlowEnabled(flow: 'ar' | 'ap', value: any): void {
    const target = this.getFlowSetupTarget(flow);
    if (target) {
      target.enabled = Boolean(value);
    }
  }

  updateFlowActive(flow: 'ar' | 'ap', value: any): void {
    const target = this.getFlowSetupTarget(flow);
    if (target) {
      target.isActive = Boolean(value);
    }
  }

  private parseMicrodepositAmount(value: string): number | null {
    const normalized = (value || '').toString().trim();
    if (!normalized) {
      return null;
    }

    const parsed = Number(normalized);
    if (!Number.isInteger(parsed) || parsed <= 0) {
      return null;
    }

    return parsed;
  }

  private notifyFlowVerifiedAndReady(flow: 'ar' | 'ap'): void {
    if (!this.isFlowAchVerified(flow)) {
      return;
    }

    const appEntityId = (this.f['entityId']?.value || this.stripeAdminEntityId || this.stripeAdminID || '').toString().trim();
    const selectedEntity = this.resolveEntityById(appEntityId);
    const entityName = (
      selectedEntity?.name
      ?? selectedEntity?.Name
      ?? this.stripeAdminEntityName
      ?? appEntityId
      ?? 'Selected entity'
    ).toString().trim();

    this.notification.showSuccessToast(`Stripe setup verified for ${entityName}. This entity is ready for Stripe transactions.`);
  }

  async submitFlowMicrodepositVerification(): Promise<void> {
    const flow = this.microdepositVerifyFlow;
    const flowLabel = flow.toUpperCase();
    const appEntityId = (this.f['entityId']?.value || this.stripeAdminEntityId || '').toString().trim();
    const appBankId = this.resolveEffectiveAppBankId();
    const setupIntentId = this.getMicrodepositVerificationSetupIntentId();

    if (!appEntityId) {
      this.notification.showWarningToast(`${flowLabel} verification requires a selected entity.`);
      return;
    }

    const payload: any = {
      appEntityId,
    };

    if (appBankId) {
      payload.appBankId = appBankId;
    }

    // Always include the rail's current SetupIntent ID for backend verification
    // (required by our contract). If missing, notify and DO NOT send.
    if (setupIntentId) {
      payload.setupIntentId = setupIntentId;
    } else {
      this.notification.showErrorToast(`${flowLabel} verification requires a SetupIntent (seti_...) but none was found. Please launch setup again or refresh status.`);
      console.warn('[Stripe Setup] Aborting microdeposit verification submit: missing setupIntentId (seti_...).');
      return;
    }

    if (this.isAmountVerificationMode()) {
      const amount1 = this.parseMicrodepositAmount(this.microdepositAmount1);
      const amount2 = this.parseMicrodepositAmount(this.microdepositAmount2);
      if (amount1 === null || amount2 === null) {
        this.notification.showWarningToast('Enter two positive microdeposit amounts in cents.');
        return;
      }

      payload.amounts = [amount1, amount2];
    } else {
      const descriptorCode = (this.microdepositDescriptorCode || '').toString().trim();
      if (!descriptorCode) {
        this.notification.showWarningToast('Enter a descriptor code (example: SM1234).');
        return;
      }

      payload.descriptorCode = descriptorCode;
    }

    this.stripeAchConfirmLoading = true;
    let verifySucceeded = false;
    console.log(`[Stripe Setup] ${flowLabel} verify request -> POST /api/stripe/rails/ap/push/verify-microdeposits`, payload);
    try {
      const result = await lastValueFrom(this.stripePaymentService.verifyApPushMicrodeposits(payload));
      console.log(`[Stripe Setup] ${flowLabel} verify response <- POST /api/stripe/rails/ap/push/verify-microdeposits`, result);
      this.captureStripeRefreshMetadata(result);
      this.pendingSetupStatus = 'pending';
      this.pendingSetupMessage = `${flowLabel} microdeposit verification returned. Refreshing setup status now...`;
      verifySucceeded = true;
    } catch (error: any) {
      const statusCode = Number(error?.status || 0);
      const exceptionMessage = (
        error?.error?.ExceptionMessage
        ?? error?.error?.exceptionMessage
        ?? error?.error?.Exception?.Message
        ?? ''
      ).toString().trim();

      if (statusCode === 400 && exceptionMessage) {
        console.error(`[Stripe Setup] ${flowLabel} verify 400 exception message:`, exceptionMessage, error);
        this.notification.showErrorToast(exceptionMessage);
      } else {
        this.notification.showErrorToast(error?.error?.Message || error?.message || `Failed to verify ${flowLabel} microdeposits.`);
      }
    } finally {
      // Always refresh setup status after the backend returns (success or error)
      try {
        await this.refreshSetupStatus(false);
        if (verifySucceeded) {
          this.notifyFlowVerifiedAndReady(flow);
          this.closeFlowMicrodepositVerifyModal();
        }
      } catch (refreshError) {
        console.warn('[Stripe Setup] Refresh after microdeposit verification failed:', refreshError);
      }
      this.stripeAchConfirmLoading = false;
    }
  }

  private getRequestedSetupFlows(): number[] {
    const activeFlowKey = this.getActiveFlowKey();
    return activeFlowKey ? [this.getFlowType(activeFlowKey)] : [];
  }

  private getFlowContract(flow: StripeSetupFlowKey) {
    return STRIPE_FLOW_CONTRACTS[flow];
  }

  private getFlowSetupTarget(flow: 'ar' | 'ap') {
    return flow === 'ar' ? this.stripeSetup.ar : this.stripeSetup.ap;
  }

  // ensure-customer payload now built via helpers; setup-intent payload via contract.setupIntentPayload

  private applySetupIntentResult(flow: StripeSetupFlowKey, response: any): void {
    const contract = this.getFlowContract(flow);
    const target = this.getFlowSetupTarget(flow);
    if (!target) {
      return;
    }

    this.mapSetupIntentResponseToFlow(contract.flowType, response);
    const setupValues = extractRuntimeSetupIntentValues(contract.flowType, response);

    if (setupValues.setupIntentClientSecret) {
      this.pendingSetupClientSecret = setupValues.setupIntentClientSecret;
      this.pendingVerificationUrl = setupValues.hostedVerificationUrl;
      this.pendingSetupMessage = contract.setupClientSecretCapturedMessage;
      this.notifyClientSecretCaptured(contract.flowLabel);
    } else if (setupValues.hostedVerificationUrl) {
      this.pendingVerificationUrl = setupValues.hostedVerificationUrl;
    }

    if (setupValues.setupIntentId) {
      target.setupIntentId = setupValues.setupIntentId;
    }
    if (setupValues.setupIntentStatus) {
      target.setupIntentStatus = setupValues.setupIntentStatus;
      target.achStatus = setupValues.setupIntentStatus;
    }
    if (setupValues.setupIntentClientSecret) {
      target.setupIntentClientSecret = setupValues.setupIntentClientSecret;
    }
    if (setupValues.hostedVerificationUrl) {
      target.hostedVerificationUrl = setupValues.hostedVerificationUrl;
    }

    // Trace: show exactly what we captured for this flow so we can diagnose UI state
    try {
      console.log('[Stripe Setup] Applied SetupIntent result', {
        flow,
        extracted: setupValues,
        pendingSetupClientSecret: this.pendingSetupClientSecret,
        pendingVerificationUrl: this.pendingVerificationUrl,
        targetFlowSnapshot: {
          setupIntentId: target.setupIntentId,
          setupIntentStatus: target.setupIntentStatus,
          setupIntentClientSecret: target.setupIntentClientSecret,
          hostedVerificationUrl: target.hostedVerificationUrl,
        },
      });
    } catch {
    }
  }

  private async runFlowSetup(flow: StripeSetupFlowKey, appEntityId: string, appBankId: string): Promise<void> {
    const contract = this.getFlowContract(flow);
    const selectedEntity = this.resolveEntityById(appEntityId);
    const ensurePayload = buildEnsureCustomerPayload(
      contract.flowType,
      appEntityId,
      selectedEntity,
      this.f['achEmail']?.value,
      contract.ensureNameFallback,
    );

    // Trace: Step 1 – Ensure Stripe customer
    this.achSetupTraceStage = 1;
    console.log('[Stripe Setup] Step 1 request -> POST /api/stripe/customers/ensure', ensurePayload);
    const ensureResult = await lastValueFrom(this.stripePaymentService.ensureCustomer(ensurePayload));
    console.log('[Stripe Setup] Step 1 response <- POST /api/stripe/customers/ensure', {
      stripeFlowType: contract.flowType,
      response: ensureResult,
    });
    this.captureStripeRefreshMetadata(ensureResult);

    if (!isSuccessfulStripeResponse(ensureResult)) {
      throw new Error(`Stripe customer ensure failed for flow ${contract.flowType}.`);
    }

    // Trace: Step 2 – Create SetupIntent
    this.achSetupTraceStage = 2;
    const setupIntentPayload = contract.setupIntentPayload({appEntityId, appBankId, flowType: contract.flowType});
    console.log('[Stripe Setup] Step 2 request -> POST /api/stripe/setup-intent', setupIntentPayload);
    const setupIntentResult = await lastValueFrom(this.stripePaymentService.createSetupIntent(setupIntentPayload));
    console.log('[Stripe Setup] Step 2 response <- POST /api/stripe/setup-intent', setupIntentResult);
    this.captureStripeRefreshMetadata(setupIntentResult);
    this.applySetupIntentResult(flow, setupIntentResult);
    this.pendingSetupStatus = 'pending';
    this.pendingSetupMessage = contract.setupPendingMessage;

    // Trace: Step 3 – Refresh setup status
    this.achSetupTraceStage = 3;
    await this.refreshSetupStatus(false);
  }

  private mapSetupIntentResponseToFlow(flow: StripeSetupFlowType, response: any): void {
    const target = flow === 1 ? this.stripeSetup.ar : this.stripeSetup.ap;
    if (!target) {
      return;
    }

    const {
      setupIntentId,
      setupIntentStatus,
      setupIntentClientSecret,
      hostedVerificationUrl,
    } = extractRuntimeSetupIntentValues(flow, response);

    if (setupIntentId) {
      target.setupIntentId = setupIntentId;
    }
    if (setupIntentStatus) {
      target.setupIntentStatus = setupIntentStatus;
      target.achStatus = setupIntentStatus;
    }
    if (setupIntentClientSecret) {
      target.setupIntentClientSecret = setupIntentClientSecret;
    }
    if (hostedVerificationUrl) {
      target.hostedVerificationUrl = hostedVerificationUrl;
    }
  }


  private notifyClientSecretCaptured(flowLabel: 'AR' | 'AP'): void {
    this.notification.showSuccessToast(`${flowLabel} setup client secret captured. You can now click Verify with Stripe.`);
  }

  private resetPendingSetupState(): void {
    this.pendingSetupStatus = 'none';
    this.pendingSetupMessage = '';
    this.pendingVerificationUrl = '';
    this.pendingSetupClientSecret = '';
    this.pendingSetupFlow = '';
  }

  loadData() {
    this.refreshSetupStatus(false).then();
  }

  async refreshSetupStatus(showToast: boolean): Promise<void> {
    this.setupLoadCompleted = false;
    // Close any prior modal/element while we refresh to avoid flicker with stale state
    this.stripeInstantVerifyDialogOpen = false;
    this.pendingSetupElementReady = false;
    const appEntityId = (this.f['entityId']?.value || this.stripeAdminEntityId || this.stripeAdminID || '').toString().trim();
    if (!appEntityId) {
      console.log('[Stripe Setup] Setup request skipped -> missing appEntityId on form load');
      this.setupLoadCompleted = true;
      if (showToast) {
        this.notification.showWarningToast('Select an entity before refreshing setup status.');
      }
      return;
    }

    const refreshEndpoint = this.lastStripeSetupRefreshEndpoint || `/api/StripeSetup/${appEntityId}`;
    console.log('[Stripe Setup] Setup request -> GET', {
      endpoint: refreshEndpoint,
      appEntityId,
    });

    try {
      const result = await lastValueFrom(this.stripePaymentService.getStripeSetup(appEntityId));
      console.log('[Stripe Setup] Setup request return <- GET', {
        endpoint: refreshEndpoint,
        response: result,
      });

      this.captureStripeRefreshMetadata(result);

      const setupEnvelope = resolveSetupEnvelope(result);
      const normalizedSetup = {
        ar: mapStripeFlowDto(setupEnvelope?.Ar),
        ap: mapStripeFlowDto(setupEnvelope?.Ap),
      };

      // Determine per-flow verified status early to prevent keeping stale client secrets
      const arVerifiedNow = String(normalizedSetup?.ar?.achStatus || '').toLowerCase() === 'verified';
      const apVerifiedNow = String(normalizedSetup?.ap?.achStatus || '').toLowerCase() === 'verified';
      const globalValues = mapStripeGlobalDto(setupEnvelope?.Global);
      const legacyRows = Array.isArray(setupEnvelope?.Rows) ? setupEnvelope.Rows : [];
      const primaryLegacyRow = legacyRows[0] ?? {};
      const fallbackBankDisplayName = (
        globalValues?.stripeBankName
        || globalValues?.bankDisplayName
        || normalizedSetup?.ar?.bankName
        || normalizedSetup?.ap?.bankName
        || primaryLegacyRow?.BankName
        || ''
      ).toString().trim();
      const fallbackBankLast4 = (
        globalValues?.bankLast4
        || normalizedSetup?.ar?.last4
        || normalizedSetup?.ap?.last4
        || primaryLegacyRow?.Last4
        || ''
      ).toString().trim();
      const mergedGlobalValues = {
        ...globalValues,
        bankDisplayName: fallbackBankDisplayName,
        stripeBankName: (globalValues?.stripeBankName || fallbackBankDisplayName || '').toString().trim(),
        bankLast4: fallbackBankLast4,
      };
      const resolvedEntityId = (
        this.stripeAdminEntityId
        || mergedGlobalValues?.appEntityId
        || this.stripeAdminID
        || ''
      ).toString().trim();
      this.isNewSetupMode = false;
      this.stripeAdminEntityName = this.resolveFriendlyEntityName(setupEnvelope?.Rows?.[0]);
      // Preserve last-known non-empty client secrets if the GET payload omits them
      const previousSetup = this.stripeSetup || {ar: this.createDefaultStripeFlow(), ap: this.createDefaultStripeFlow()} as any;
      const mergedSetup = {
        ar: {
          ...previousSetup.ar,
          ...normalizedSetup.ar,
          // If AR is verified, do NOT carry forward any client secret
          setupIntentClientSecret: arVerifiedNow
            ? ''
            : (
              (normalizedSetup?.ar?.setupIntentClientSecret || '')
              || (previousSetup?.ar?.setupIntentClientSecret || '')
            ).toString().trim(),
        },
        ap: {
          ...previousSetup.ap,
          ...normalizedSetup.ap,
          // If AP is verified, do NOT carry forward any client secret
          setupIntentClientSecret: apVerifiedNow
            ? ''
            : (
              (normalizedSetup?.ap?.setupIntentClientSecret || '')
              || (previousSetup?.ap?.setupIntentClientSecret || '')
            ).toString().trim(),
        },
      };
      this.stripeSetup = mergedSetup;
      this.stripeGlobal = mergedGlobalValues;

      const resolvedAppBankId = this.resolveEffectiveAppBankId({AppBankId: mergedGlobalValues?.appBankId});
      const resolvedBankName = (mergedGlobalValues?.stripeBankName || mergedGlobalValues?.bankDisplayName || '').toString().trim();
      const scopedEntityType = this.scopedFlow === 'AR' ? '1' : this.scopedFlow === 'AP' ? '2' : '';
      const currentEntityType = (this.f['entityType']?.value || '').toString().trim();
      const resolvedEntityType = currentEntityType || scopedEntityType;

      this.stripeAdminForm.patchValue({
        bankId: resolvedAppBankId,
        bankName: resolvedBankName,
        entityId: resolvedEntityId,
        entityType: resolvedEntityType,
      });

      if (resolvedAppBankId) {
        this.loadBankById(resolvedAppBankId);
      }

      const selectedEntity = this.resolveEntityById(resolvedEntityId);
      this.applyEntitySelection(selectedEntity);
      this.syncVerifiedGlobalControlLock();

      const activeFlowKey = this.getActiveFlowKey();
      const activeFlow = activeFlowKey ? normalizedSetup?.[activeFlowKey] : null;
      const activeFlowVerified = activeFlowKey ? this.isFlowAchVerified(activeFlowKey) : false;
      const activeFlowClientSecret = (activeFlow?.setupIntentClientSecret ?? '').toString().trim();
      const activeFlowVerificationUrl = (activeFlow?.hostedVerificationUrl ?? '').toString().trim();

      // Do not auto-open any modal here. Only surface a notification if pending verification exists.
      // Also avoid populating pending client secret/URL here to prevent any unintended modal behavior.
      this.stripeInstantVerifyDialogOpen = false;
      this.pendingSetupElementReady = false;

      const hasPendingVerification = !activeFlowVerified && (Boolean(activeFlowClientSecret) || Boolean(activeFlowVerificationUrl));
      if (!hasPendingVerification) {
        this.resetPendingSetupState();
        if (showToast) {
          this.notification.showInfoToast('Stripe setup status refreshed.');
        }
      } else {
        // Notify the user that verification is still pending; instruct to use in-app microdeposit verification.
        if (showToast) {
          const flowLabel = activeFlowKey === 'ap' ? 'AP' : 'AR';
          this.notification.showInfoToast(`${flowLabel} pending verification detected. Use Verify Microdeposits to complete.`);
        }
      }
    } catch (error: any) {
      const httpError = error as HttpErrorResponse;
      console.log('[Stripe Setup] Setup request error -> GET', {
        endpoint: refreshEndpoint,
        appEntityId,
        status: httpError?.status,
        message: httpError?.message,
        error: httpError?.error,
      });
      if (showToast) {
        this.notification.showErrorToast(httpError?.error?.Message || httpError?.message || 'Failed to refresh Stripe setup status.');
      }
    } finally {
      this.setupLoadCompleted = true;
    }
  }

  private captureStripeRefreshMetadata(result: any): void {
    const process = (result?.Process || '').toString().trim();
    const setupProfileEndpoint = (result?.RefreshEndpoints?.SetupProfile || '').toString().trim();

    if (process) {
      this.lastStripeProcess = process;
    }
    if (setupProfileEndpoint) {
      this.lastStripeSetupRefreshEndpoint = setupProfileEndpoint;
    }

    if (process || setupProfileEndpoint) {
      console.log('[Stripe Setup] Refresh metadata', {
        process,
        setupProfileEndpoint,
      });
    }
  }

  private toTrimmedOrNull(value: any): string | null {
    const normalized = (value || '').toString().trim();
    return normalized ? normalized : null;
  }

  private buildFlowSetupPayload(flow: number, appEntityId: string, appBankId: string): any {
    const currentFlow = flow === 1 ? this.stripeSetup.ar : this.stripeSetup.ap;
    return {
      appEntityId,
      appBankId,
      flow,
      enabled: Boolean(currentFlow?.enabled),
      isActive: Boolean(currentFlow?.isActive),
      stripeCustomerId: this.toTrimmedOrNull(currentFlow?.stripeCustomerId),
      stripePaymentMethodId: this.toTrimmedOrNull(currentFlow?.stripePaymentMethodId),
      methodType: this.toTrimmedOrNull(currentFlow?.methodType),
      setupIntentId: this.toTrimmedOrNull(currentFlow?.setupIntentId),
      setupIntentStatus: this.toTrimmedOrNull(currentFlow?.setupIntentStatus),
      clientSecret: this.toTrimmedOrNull(currentFlow?.setupIntentClientSecret),
      hostedVerificationUrl: this.toTrimmedOrNull(currentFlow?.hostedVerificationUrl),
      achStatus: this.toTrimmedOrNull(currentFlow?.achStatus),
    };
  }

  isFlowActivationUpdateLoading(flow: 'ar' | 'ap'): boolean {
    return this.flowActivationUpdateLoading === flow;
  }

  async updateFlowActivationSettings(flowKey: 'ar' | 'ap'): Promise<void> {
    const flow = this.getFlowType(flowKey);
    const appEntityId = (this.f['entityId']?.value || this.stripeAdminEntityId || '').toString().trim();
    const appBankId = this.resolveEffectiveAppBankId();

    if (!appBankId || !appEntityId) {
      this.notification.showWarningToast('Select an entity with a bank before updating Stripe setup.');
      return;
    }

    try {
      this.flowActivationUpdateLoading = flowKey;
      const setupPayload = this.buildFlowSetupPayload(flow, appEntityId, appBankId);
      console.log('[Stripe Setup] Activation update request -> PUT /api/StripeSetup', setupPayload);
      await lastValueFrom(this.table.put('StripeSetup', '', setupPayload));
      await this.refreshSetupStatus(false);
      const flowLabel = flowKey === 'ar' ? 'AR' : 'AP';
      this.notification.showSuccessToast(`${flowLabel} Active/Enabled updated.`);
    } catch (error: any) {
      this.notification.showErrorToast(error?.error?.Message || error?.message || 'Error updating Active/Enabled values.');
    } finally {
      this.flowActivationUpdateLoading = '';
    }
  }


  saveClose() {
    this.onSave(true).then();
  }

  async onSave(closeAfterSave = false) {
    this.submitted = true;
    const appEntityId = (this.f['entityId']?.value || this.stripeAdminEntityId || '').toString().trim();
    const appBankId = this.resolveEffectiveAppBankId();
    if (!appBankId || !appEntityId) {
      this.notification.showWarningToast('Select an entity with a bank before saving Stripe setup.');
      return;
    }

    this.stripeAdminID = appEntityId;

    try {
      const flowsToSave = this.getRequestedSetupFlows();
      for (const flow of flowsToSave) {
        const setupPayload = this.buildFlowSetupPayload(flow, appEntityId, appBankId);
        console.log('[Stripe Setup] Save request -> PUT /api/StripeSetup', setupPayload);
        await lastValueFrom(this.table.put('StripeSetup', '', setupPayload));
      }

      this.notification.showSuccessToast($localize`:Saved@@saved:Saved`);
      if (closeAfterSave) {
        this.onClose();
      }
    } catch (error: any) {
      this.notification.showErrorToast(error?.error?.Message || error?.message || 'Error saving Stripe setup.');
    }
  }

  async autoConfigureStripePaymentMethodIds() {
    this.stripeAutoConfigLoading = true;
    try {
      await this.refreshSetupStatus(true);
    } finally {
      this.stripeAutoConfigLoading = false;
    }
  }


  openStripeInstantVerifyDialog(): void {
    const hasVerificationAction = Boolean((this.pendingVerificationUrl || '').toString().trim())
      || Boolean((this.pendingSetupClientSecret || '').toString().trim());

    if (!hasVerificationAction) {
      this.notification.showWarningToast('No pending Stripe verification action is available.');
      return;
    }

    this.stripeInstantVerifyDialogOpen = true;
    this.pendingSetupElementReady = false;
    // Track whether this modal run is for instant verification (client_secret path)
    try {
      const hasUrl = Boolean((this.pendingVerificationUrl || '').toString().trim());
      const hasSecret = Boolean((this.pendingSetupClientSecret || '').toString().trim());
      // Deterministic selection: prefer client_secret when both exist
      (this as any).pendingSetupIsInstant = hasSecret;
    } catch {
      // noop
    }
  }

  closeStripeInstantVerifyDialog(): void {
    const wasInstant = Boolean((this as any).pendingSetupIsInstant);
    const flow = this.pendingSetupFlow || this.getActiveFlowKey() || '';
    // Reset modal state first
    this.stripeInstantVerifyDialogOpen = false;
    this.pendingSetupElementReady = false;
    // If this modal was running instant verification (client_secret path),
    // trigger a refresh on close so the main form pulls updated values.
    if (wasInstant) {
      // Fire-and-forget; do not block UI close
      Promise.resolve()
        .then(() => this.refreshSetupStatus(false))
        .catch(() => {/* swallow */
        });
    }
    // Clear the flag
    (this as any).pendingSetupIsInstant = false;
    if (flow === 'ar' || flow === 'ap') {
      this.verifyInFlight[flow] = false;
    }
    this.pendingSetupFlow = '';
  }

  onPendingSetupPaymentElementReady(): void {
    this.pendingSetupElementReady = true;
    try {
      console.log('[Stripe Setup] Payment Element ready', {
        flow: this.pendingSetupFlow || this.getActiveFlowKey(),
        hasClientSecret: Boolean((this.pendingSetupClientSecret || '').toString().trim()),
      });
    } catch {
    }
  }

  async continuePendingSetupVerification(): Promise<void> {
    if (this.stripeAchConfirmLoading) {
      return;
    }
    // Lock immediately to prevent any re-entry (double clicks or async races)
    this.stripeAchConfirmLoading = true;
    const verificationFlow = this.pendingSetupFlow || this.getActiveFlowKey() || 'ar';
    const clientSecret = (this.pendingSetupClientSecret || '').toString().trim();
    const verificationUrl = (this.pendingVerificationUrl || '').toString().trim();

    // Prefer client_secret (instant verify) when available
    if (clientSecret) {
      const paymentElementRef = this.pendingSetupPaymentElement;
      const stripeElements = paymentElementRef?.elements;
      if (!paymentElementRef || !stripeElements) {
        this.notification.showWarningToast('Stripe verification form is still loading. Please wait and try again.');
        return;
      }

      try {
        const appEntityId = (this.f['entityId'].value || this.stripeAdminEntityId || '').toString().trim();
        const selectedEntity = this.resolveEntityById(appEntityId);
        const customerName = (
          selectedEntity?.name ??
          selectedEntity?.Name ??
          this.stripeAdminEntityName ??
          appEntityId
        ).toString().trim();
        const customerEmail = (
          selectedEntity?.email ??
          selectedEntity?.Email ??
          this.f['achEmail']?.value ??
          ''
        ).toString().trim();

        const submitResult = await stripeElements.submit();
        if (submitResult?.error) {
          this.notification.showErrorToast(submitResult.error.message || 'Please complete the Stripe verification form.');
          return;
        }

        const confirmOptions: any = {
          elements: stripeElements,
          clientSecret,
          redirect: 'if_required',
          confirmParams: {
            payment_method_data: {
              billing_details: {
                name: customerName || undefined,
                email: customerEmail || undefined,
              },
            },
          },
        };

        const intentType = getClientSecretIntentType(clientSecret);
        const confirmResult: any = intentType === 'payment'
          ? await lastValueFrom(this.stripeService.confirmPayment(confirmOptions))
          : await lastValueFrom(this.stripeService.confirmSetup(confirmOptions));
        const confirmError = confirmResult?.error;

        if (confirmError) {
          this.instantVerifyFailed[verificationFlow] = true;
          this.notification.showErrorToast(confirmError?.message || 'Stripe verification was not completed.');
          return;
        }

        // Analyze intent status explicitly and take exactly one path
        const setupIntent = confirmResult?.setupIntent;
        const paymentIntent = confirmResult?.paymentIntent;
        const status: string = (
          setupIntent?.status || paymentIntent?.status || ''
        ).toString();

        // Close modal, clear pending fields so nothing auto‑reopens
        this.instantVerifyFailed[verificationFlow] = false;
        this.closeStripeInstantVerifyDialog();
        this.pendingSetupClientSecret = '';
        this.pendingVerificationUrl = '';

        if (status === 'succeeded' || status === 'processing' || status === 'requires_capture') {
          this.pendingSetupStatus = 'ready';
          this.pendingSetupMessage = 'Verification submitted. Refreshing setup status now...';
          try {
            await this.refreshSetupStatus(false);
          } catch { /* ignore */
          }
          if (this.isFlowAchVerified(verificationFlow)) {
            this.notifyFlowVerifiedAndReady(verificationFlow);
          } else {
            this.notification.showSuccessToast('Stripe verification submitted. If still pending, complete microdeposit verification.');
          }
        } else if (status === 'requires_action' || status === 'requires_payment_method') {
          this.instantVerifyFailed[verificationFlow] = true;
          try {
            await this.refreshSetupStatus(false);
          } catch { /* ignore */
          }
          this.notification.showInfoToast('Additional verification is required. Use Verify Microdeposits to complete.');
        } else {
          try {
            await this.refreshSetupStatus(false);
          } catch { /* ignore */
          }
          this.notification.showInfoToast('Verification submitted. Refresh to see latest status, or verify microdeposits if required.');
        }
      } catch (error: any) {
        this.instantVerifyFailed[verificationFlow] = true;
        this.notification.showErrorToast(error?.error?.Message || error?.message || 'Failed to continue Stripe verification.');
      } finally {
        this.stripeAchConfirmLoading = false;
      }
      return;
    }

    // Hosted/embedded modal fallback is not used; notify only so user can use our micro‑deposit flow
    if (verificationUrl) {
      this.closeStripeInstantVerifyDialog();
      this.pendingSetupClientSecret = '';
      this.pendingVerificationUrl = '';
      this.notification.showInfoToast('Pending verification detected. Use Verify Microdeposits to complete.');
      this.stripeAchConfirmLoading = false;
      try {
        await this.refreshSetupStatus(false);
      } catch { /* ignore */
      }
      return;
    }

    // No pending action
    this.notification.showWarningToast('No pending Stripe verification action is available.');
    this.stripeAchConfirmLoading = false;
  }

  async launchAchSetup() {
    const appEntityId = (this.f['entityId'].value || this.stripeAdminEntityId || '').toString().trim();
    const appBankId = this.resolveEffectiveAppBankId();
    const activeFlowKey = this.getActiveFlowKey();

    if (!appEntityId) {
      this.notification.showWarningToast('Entity Id is required for ACH setup.');
      this.achSetupStatusType = 'warning';
      this.achSetupStatusMessage = 'Entity Id is required before launching ACH setup.';
      return;
    }

    if (!appBankId) {
      this.notification.showWarningToast('A linked bank is required for ACH setup. Select an entity with a bank mapping.');
      this.achSetupStatusType = 'warning';
      this.achSetupStatusMessage = 'A linked bank is required before launching ACH setup.';
      return;
    }

    if (!activeFlowKey) {
      this.notification.showWarningToast('Select a single flow (AR or AP) before launching setup.');
      this.achSetupStatusType = 'warning';
      this.achSetupStatusMessage = 'A single flow is required. Choose AR or AP and retry Launch ACH Setup.';
      return;
    }

    this.stripeAchSetupLoading = true;
    this.achSetupTracing = true;
    this.achSetupTraceStage = 1; // starting with ensure customer
    this.achSetupStatusType = 'info';
    this.achSetupStatusMessage = 'Starting Stripe setup...';
    try {
      await this.runFlowSetup(activeFlowKey, appEntityId, appBankId);
      const contract = this.getFlowContract(activeFlowKey);
      this.notification.showSuccessToast(contract.setupStartedToast);
      this.achSetupStatusType = 'success';
      this.achSetupStatusMessage = contract.setupStartedStatusMessage;
    } catch (error: any) {
      const apiError = error?.error?.Message || error?.error?.message || error?.message || 'Failed to launch ACH setup.';
      this.notification.showErrorToast(apiError);
      this.achSetupStatusType = 'danger';
      this.achSetupStatusMessage = apiError;
    } finally {
      this.stripeAchSetupLoading = false;
      this.achSetupTraceStage = 4; // done
      // Hide the trace shortly after completion to keep UI clean
      setTimeout(() => {
        this.achSetupTracing = false;
        this.achSetupTraceStage = 0;
      }, 5000);
      // Safety: ensure we refresh after the launch sequence completes, even
      // if inner steps already refreshed. This guarantees the instruction
      // badges and card details reflect the latest server state.
      try {
        await this.refreshSetupStatus(false);
      } catch {
        // swallow
      }
    }
  }


  resetForm() {
    this.achSetupStatusType = '';
    this.achSetupStatusMessage = '';
    this.instantVerifyFailed.ar = false;
    this.resetPendingSetupState();
    this.stripeAdminForm.patchValue({
      achAccountHolderName: '',
      achEmail: '',
      achRoutingNumber: '',
      achAccountNumber: '',
      achAccountHolderType: 'individual',
    });
    if (this.stripeAdminID) {
      this.loadData();
      return;
    }

    this.stripeAdminEntityId = '';
    this.stripeAdminEntityName = '';
    this.stripeSetup = {
      ar: this.createDefaultStripeFlow(),
      ap: this.createDefaultStripeFlow(),
    };
    this.stripeAdminForm.patchValue({
      bankId: '',
      bankName: '',
      entityId: '',
      entityType: '',
      achAccountHolderName: '',
      achEmail: '',
      achRoutingNumber: '',
      achAccountNumber: '',
      achAccountHolderType: 'individual',
    });
    this.syncVerifiedGlobalControlLock();
  }

  // --- Instruction area helpers (Steps 1–3) ---
  private getInstructionCurrentFlow(): 'ar' | 'ap' {
    return (this.getActiveFlowKey() || 'ar') as 'ar' | 'ap';
  }

  isInstructionStep1Complete(): boolean {
    const entityId = (this.f['entityId']?.value || this.stripeAdminEntityId || '').toString().trim();
    const scope = (this.f['entityType']?.value || '').toString().trim();
    return !!entityId && !!scope;
  }

  isInstructionStep2Complete(): boolean {
    const flow = this.getInstructionCurrentFlow();
    const setup: any = this.getSetupFlow(flow);
    const hasIntentId = ((setup?.setupIntentId || '').toString().trim().length > 0);
    const hasClientSecret = ((setup?.setupIntentClientSecret || '').toString().trim().length > 0);
    const hasHostedUrl = ((setup?.hostedVerificationUrl || '').toString().trim().length > 0);
    return hasIntentId || hasClientSecret || hasHostedUrl;
  }

  isInstructionStep3Complete(): boolean {
    const flow = this.getInstructionCurrentFlow();
    return this.isFlowAchVerified(flow);
  }

  private getInstructionCurrentStepNumber(): 1 | 2 | 3 {
    if (!this.isInstructionStep1Complete()) {
      return 1;
    }
    if (!this.isInstructionStep2Complete()) {
      return 2;
    }
    if (!this.isInstructionStep3Complete()) {
      return 3;
    }
    return 3;
  }

  getInstructionBadgeClass(step: 1 | 2 | 3): string {
    const current = this.getInstructionCurrentStepNumber();
    if ((step === 1 && this.isInstructionStep1Complete())
      || (step === 2 && this.isInstructionStep2Complete())
      || (step === 3 && this.isInstructionStep3Complete())) {
      return 'text-success-emphasis bg-success-subtle border border-success-subtle';
    }
    if (step === current) {
      return 'text-primary-emphasis bg-primary-subtle border border-primary-subtle';
    }
    return 'text-secondary-emphasis bg-secondary-subtle border border-secondary-subtle';
  }

  onClose() {
    this.router.navigate(['/admin/stripe-admin-list']).then();
  }

  deleteItem() {
    const appEntityId = (this.f['entityId']?.value || this.stripeAdminEntityId || '').toString().trim();
    const entityName = this.selectedEntityDisplayName;
    const flowType = Number(this.f['entityType']?.value);

    if (!appEntityId) {
      this.notification.showWarningToast('Select an entity before clearing Stripe setup.');
      return;
    }

    if (flowType !== 1 && flowType !== 2) {
      this.notification.showWarningToast('Select AR or AP before deleting Stripe setup.');
      return;
    }

    const flowLabel = flowType === 1 ? 'AR' : 'AP';
    this.confirmDialogService.confirmThis(
      [
        `Are you sure you want to clear ${flowLabel} Stripe setup for ${entityName}?`,
        'This deletes Setup Intent / Payment Intent for this Entity and Scope.',
      ],
      () => {
        lastValueFrom(this.stripePaymentService.deleteStripeSetup(appEntityId, flowType))
          .then(() => {
            this.notification.showSuccessToast($localize`:Deleted @@deleted:Deleted`);
            this.onClose();
          })
          .catch((error: HttpErrorResponse) => {
            this.notification.showErrorToast(error?.error?.Message || error.message || 'Error clearing Stripe setup.');
          });
      },
      () => {
        this.notification.showSuccessToast($localize`:Delete Canceled @@deleteCanceled:Delete Canceled`);
      }
    );
  }
}
